import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Zap, Package, IndianRupee, UserCheck, Bike, Clock, UserPlus, Pencil, History } from "lucide-react";
import { api } from "@/lib/api";
import { inr, inrCompact, num, fmtDate, initials } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { KpiCard } from "@/components/KpiCard";
import { StatusBadge } from "@/components/StatusBadge";
import { EditDailyOrderModal } from "@/components/EditDailyOrderModal";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useApp } from "@/context/AppContext";

export default function DailyOperations() {
  const [kpi, setKpi] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [recent, setRecent] = useState([]);
  const [editRec, setEditRec] = useState(null);
  const { openQuickEntry, role, can } = useApp();
  const canEdit = can("daily.edit");
  const nav = useNavigate();

  const loadRecent = useCallback(() => {
    api.get("/daily-recent?limit=15").then(({ data }) => setRecent(data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/dashboard/kpis").then(({ data }) => setKpi(data));
    api.get("/dp/search?q=FE").then(({ data }) => setResults(data));
    loadRecent();
  }, [loadRecent]);
  useEffect(() => {
    const h = () => loadRecent();
    window.addEventListener("ct-refresh", h);
    return () => window.removeEventListener("ct-refresh", h);
  }, [loadRecent]);
  useEffect(() => {
    if (!q) { api.get("/dp/search?q=FE").then(({ data }) => setResults(data)); return; }
    const t = setTimeout(() => api.get(`/dp/search?q=${encodeURIComponent(q)}`).then(({ data }) => setResults(data)), 200);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div>
      <PageHeader title="Daily Operations" subtitle="Search → enter orders → auto-calculate → save. Target < 15s per DP." />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
        {kpi && <>
          <KpiCard testId="ops-active" label="Active DP" value={num(kpi.active_dp)} icon={UserCheck} accent="emerald" />
          <KpiCard testId="ops-orders" label="Orders Today" value={num(kpi.orders_today)} icon={Package} accent="primary" />
          <KpiCard testId="ops-earnings" label="Earnings Today" value={inrCompact(kpi.earnings_today)} icon={IndianRupee} accent="emerald" />
          <KpiCard testId="ops-ev-due" label="EV Rent Due" value={inrCompact(kpi.ev_rent_outstanding)} icon={Bike} accent="rose" />
          <KpiCard testId="ops-new" label="New Onboarding" value={num(kpi.new_onboarding_month)} icon={UserPlus} accent="violet" />
          <KpiCard testId="ops-inactive" label="Inactive DP" value={num(kpi.inactive_dp)} icon={Clock} accent="amber" />
        </>}
      </div>

      <div className="mt-6 rounded-xl border border-border bg-card p-5">
        <div className="relative mb-4">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="ops-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search Delivery Partner by FE ID, mobile, name or location…"
            className="h-14 pl-12 text-base" autoFocus />
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {results.map((r) => (
            <div key={r.id} data-testid={`ops-card-${r.fe_id}`} className="flex items-center gap-3 rounded-lg border border-border bg-secondary/40 p-3 transition hover:border-primary/40">
              <button onClick={() => nav(`/dp/${r.id}`)} className="flex min-w-0 flex-1 items-center gap-3 text-left">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary">{initials(r.full_name)}</div>
                <div className="min-w-0"><p className="truncate text-sm font-semibold">{r.full_name}</p><p className="truncate text-xs text-muted-foreground font-mono">{r.fe_id} · {r.location}</p></div>
              </button>
              <div className="text-right"><StatusBadge status={r.status} /></div>
              <Button size="sm" onClick={() => openQuickEntry(r)} data-testid={`ops-quick-${r.fe_id}`} className="h-9 active:scale-95"><Zap className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 overflow-hidden rounded-xl border border-border bg-card" data-testid="recent-entries-card">
        <div className="flex items-center gap-2 border-b border-border px-5 py-3.5">
          <History className="h-4 w-4 text-primary" />
          <h3 className="font-head text-sm font-bold uppercase tracking-wide">Recent Entries</h3>
          <span className="text-xs text-muted-foreground">— latest daily order records across all DPs</span>
        </div>
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent">
            <TableHead>Date</TableHead><TableHead>Delivery Partner</TableHead><TableHead>Location</TableHead>
            <TableHead className="text-right">Orders</TableHead><TableHead className="text-right">Multi</TableHead><TableHead className="text-right">DP Earnings</TableHead>
            <TableHead className="text-right">Franchise</TableHead>
            {canEdit && <TableHead className="text-right">Edit</TableHead>}
          </TableRow></TableHeader>
          <TableBody>
            {recent.length === 0 && <TableRow><TableCell colSpan={canEdit ? 8 : 7} className="text-center text-muted-foreground">No entries yet</TableCell></TableRow>}
            {recent.map((d) => (
              <TableRow key={d.id} data-testid={`recent-row-${d.id}`}>
                <TableCell className="whitespace-nowrap">{fmtDate(d.date)}</TableCell>
                <TableCell>
                  <button onClick={() => nav(`/dp/${d.dp_id}`)} className="text-left">
                    <p className="text-sm font-semibold hover:text-primary">{d.dp_name}</p>
                    <p className="font-mono text-xs text-muted-foreground">{d.fe_id}</p>
                  </button>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{d.location}</TableCell>
                <TableCell className="text-right tabular font-semibold">{d.orders}</TableCell>
                <TableCell className="text-right tabular text-sky-500" data-testid={`recent-multi-${d.id}`}>{d.multi_orders || 0}</TableCell>
                <TableCell className="text-right tabular font-semibold text-emerald-500">{inr(d.total_earnings)}</TableCell>
                <TableCell className="text-right tabular text-violet-500">{d.franchise_earning != null ? inr(d.franchise_earning) : "-"}</TableCell>
                {canEdit && (
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" data-testid={`recent-edit-${d.id}`} onClick={() => setEditRec(d)} className="h-8 w-8 p-0">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <EditDailyOrderModal record={editRec} onClose={() => setEditRec(null)} onSaved={loadRecent} />
    </div>
  );
}
